export const resizeImage = async (
    file: File,
    maxWidth: number = 1200,
    maxHeight: number = 1200,
    initialQuality: number = 0.9,
    maxSize: number = 500 * 1024 // 500KB
): Promise<File | null> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);

        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                resolve(null);
                return;
            }

            let width = img.width;
            let height = img.height;
            const aspectRatio = width / height;

            if (width > maxWidth || height > maxHeight) {
                if (aspectRatio > 1) {
                    width = maxWidth;
                    height = Math.round(width / aspectRatio);
                } else {
                    height = maxHeight;
                    width = Math.round(height * aspectRatio);
                }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            let quality = initialQuality;

            const tryCompress = () => {
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            const resizedFile = new File([blob], file.name, { type: file.type });
                            if (resizedFile.size <= maxSize || quality <= 0.1) {
                                resolve(resizedFile);
                            } else {
                                quality -= 0.1;
                                tryCompress();
                            }
                        } else {
                            resolve(null);
                        }
                    },
                    "image/jpeg",
                    quality
                );
            };

            tryCompress();
        };

        img.onerror = () => {
            resolve(null);
        };
    });
};

export const resizeImageToFitMinimumSize = async (
    file: File,
    minWidth: number = 200,
    minHeight: number = 200
): Promise<File | null> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);

        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                resolve(null);
                return;
            }

            let width = img.width;
            let height = img.height;

            if (width < minWidth || height < minHeight) {
                const aspectRatio = width / height;

                if (width < minWidth && height < minHeight) {
                    width = minWidth;
                    height = Math.round(width / aspectRatio);
                    if (height < minHeight) {
                        height = minHeight;
                        width = Math.round(height * aspectRatio);
                    }
                } else if (width < minWidth) {
                    width = minWidth;
                    height = Math.round(width / aspectRatio);
                } else if (height < minHeight) {
                    height = minHeight;
                    width = Math.round(height * aspectRatio);
                }
            }

            canvas.width = width;
            canvas.height = height;

            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const resizedFile = new File([blob], file.name, { type: file.type });
                        resolve(resizedFile);
                    } else {
                        resolve(null);
                    }
                },
                "image/jpeg",
                0.9
            );
        };

        img.onerror = () => {
            resolve(null);
        };
    });
};


export function base64ToFile(base64: string, filename: string, mimeType = 'image/jpeg'): File {
    const arr = base64.split(',');
    const byteString = atob(arr[1]);
    const mime = arr[0].match(/:(.*?);/)?.[1] || mimeType;
  
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
  
    return new File([ab], filename, { type: mime });
  }